// src/components/player-profile/AnalyticsGrid.js
//
// Extracted from inline module-level definition in PlayerProfileModal.js
// per IMPORTANT batch 8 commit 8h (CLOSES BATCH 8). 3-row × 4-tile
// analytics grid rendered in the modal's full-mode persistent header.
//
// Extraction discipline:
//   - Zero closure→prop conversion: all closures captured by the inline
//     definition were AnalyticsGrid-locals (derived from props), not
//     parent-scope refs.
//   - formatAvg + formatEra rename-alias wrappers deleted in 8k.2b;
//     call sites now use canonical formatBattingAvg / formatERA
//     directly (cross-codebase rename-alias consolidation per
//     §14.test-infra.4 revised disposition).
//   - Inline Cell and RankCell sub-components stay inline; their closure
//     refs are AnalyticsGrid-locals.
//
// Two quality fixes folded in (same render path, single coherent
// surface, audit-CC reviewed):
//   - Removed dead prop `advancedRankings` from destructure. Declared
//     but zero body references; component reads rankings from
//     `player.analytics.rankings`, not from this prop.
//   - Fixed inconsistent optional chaining: `player.info?.position` →
//     `player?.info?.position`. Eliminates latent throw when `player`
//     is undefined; matches surrounding defensive accesses elsewhere
//     in the body.
import React from 'react';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { formatContractDisplay } from '../../services/contractUtils';
import { dynastyTheme } from '../../services/colorService';

// NOTE (C44 death, 2026-07-14): `personalizedValue` is no longer read here — no
// surface in this grid consumes the optimizer fit score. The parent's fetch and
// prop remain (other non-value-tile surfaces may consume it); an unused prop
// pass-through is harmless.
const AnalyticsGrid = ({ player, isPitcher, leagueId, statsSeasonYear }) => {
    const season = player?.stats?.season;
    const pitcher = isPitcher;
    const stats = pitcher ? season?.pitching || {} : season?.hitting || {};
    const analytics = player?.analytics || {};
    const financial = player?.league_context?.financial || {};
    const isOwned = player?.league_context?.status === 'owned';

    // Accent border colors from theme. Real export path is dynastyTheme.tokens.colors
    // (NOT .colors). These tokens resolve to var(--team-primary, ...) etc., which carry
    // the team-customization default at the CSS level — no JS hex fallback (Rule 9).
    const accent = {
      info: dynastyTheme.tokens.colors.info,
      primary: dynastyTheme.tokens.colors.primary,
      success: dynastyTheme.tokens.colors.success,
      error: dynastyTheme.tokens.colors.error,
      neutral: 'rgba(55,65,81,0.6)',
    };
    const cardBorder = 'rgba(55,65,81,0.6)';

    // === Row 1: Performance snapshot ===

    // Tile 1: Power (hitter) / Stuff (pitcher) — triple value (array for spread layout)
    const powerValue = (() => {
      if (pitcher) {
        const k9 = stats.innings_pitched ? ((stats.strikeouts_pitched || 0) * 9 / stats.innings_pitched).toFixed(1) : null;
        const era = stats.era != null ? formatERA(stats.era) : null;
        const fip = stats.innings_pitched
          ? (((13 * (stats.home_runs_allowed || 0) + 3 * ((stats.walks_allowed || 0) + (stats.hit_by_pitch || 0)) - 2 * (stats.strikeouts_pitched || 0)) / stats.innings_pitched) + 3.2).toFixed(2)
          : null;
        const parts = [k9 && `${k9} K/9`, era && `${era} ERA`, fip && `${fip} FIP`].filter(Boolean);
        return parts.length > 0 ? parts : '--';
      }
      const hr = stats.home_runs != null ? stats.home_runs : null;
      const slg = stats.slg != null ? formatBattingAvg(stats.slg) : null;
      const iso = (stats.slg != null && stats.batting_avg != null) ? formatBattingAvg(stats.slg - stats.batting_avg) : null;
      const parts = [hr != null && `${hr} HR`, slg && `${slg} SLG`, iso && `${iso} ISO`].filter(Boolean);
      return parts.length > 0 ? parts : '--';
    })();

    // Tile 2: Discipline (hitter) / Command (pitcher) — triple value (array for spread layout)
    const discValue = (() => {
      if (pitcher) {
        const bb9 = stats.innings_pitched ? (((stats.walks_allowed || 0) * 9) / stats.innings_pitched).toFixed(1) : null;
        const whip = stats.whip != null ? formatBattingAvg(stats.whip) : null;
        const kbb = stats.walks_allowed ? ((stats.strikeouts_pitched || 0) / stats.walks_allowed).toFixed(2) : null;
        const parts = [bb9 && `${bb9} BB/9`, whip && `${whip} WHIP`, kbb && `${kbb} K/BB`].filter(Boolean);
        return parts.length > 0 ? parts : '--';
      }
      const pa = (stats.at_bats || 0) + (stats.walks || 0);
      const bbPct = pa ? (((stats.walks || 0) / pa) * 100).toFixed(1) : null;
      const kPct = pa ? (((stats.strikeouts || 0) / pa) * 100).toFixed(1) : null;
      const bbk = stats.strikeouts ? ((stats.walks || 0) / stats.strikeouts).toFixed(2) : null;
      const parts = [bbPct && `${bbPct}% BB`, kPct && `${kPct}% K`, bbk && `${bbk} BB/K`].filter(Boolean);
      return parts.length > 0 ? parts : '--';
    })();

    // Tile 3: Consistency
    const consistencyScore = analytics.consistency?.score;
    const consistencyGrade = analytics.consistency?.grade || '--';
    const consistencyMetric = analytics.consistency?.metric;
    const consistencyBlowups = analytics.consistency?.blow_ups;
    // DD-RO-BUGS6-0725 B4: name the metric the grade actually measures. "47/100 · Game-to-game
    // stability" was opaque — 47 is a PERCENT of quality starts (population-graded), not an x/100
    // rating. The sub-line now states the served consistency.metric in plain terms for every role;
    // relievers also get the blow-up (3+ ER) DISPLAY diagnostic.
    const consistencySub = consistencyScore == null
      ? 'Game-to-game stability'
      : consistencyMetric === 'scoreless_appearance_rate'
        ? `${consistencyScore}% scoreless${consistencyBlowups != null ? `\n${consistencyBlowups} blow-ups (3+ ER)` : ''}`
        : consistencyMetric === 'quality_start_rate'
          ? `${consistencyScore}% quality starts\n(6+ IP, ≤3 ER)`
          : consistencyMetric === 'onbase_game_rate'
            ? `${consistencyScore}% of games on base\n(hit, walk, or HBP)`
            : `${consistencyScore}%\nGame-to-game stability`;

    // Tile 4: Trend (works for both hitters and pitchers)
    const trendData = analytics.trend;
    const overallTrend = trendData?.overall_trend || '--';
    const trendTrends = trendData?.trends || {};

    // Get top 2 trending stats with direction arrows
    const trendEntries = Object.entries(trendTrends).slice(0, 2);
    const trendDetails = trendEntries
      .map(([stat, data]) => `${data?.symbol || '→'} ${stat.replace(/_/g, ' ')}`)
      .join(' · ');

    // L14 key stat as subtext
    const trendSub = (() => {
      const r = player?.stats?.rolling_14_day;
      if (!r) return '';
      if (pitcher) {
        const era = r?.pitching?.era;
        return era != null ? `${formatERA(era)} L14 ERA` : '';
      }
      const avg = r?.hitting?.batting_avg;
      return avg != null ? `${formatBattingAvg(avg)} L14 AVG` : '';
    })();

    // === Row 2: Rankings & value ===
    const rankings = analytics?.rankings;

    // Tile 5: MLB Rank — overall scope. NOTE: rankings.categories.position is a
    // LIST (one record per position the player ranks at), so the old
    // `categories.position?.rank` read `.rank` on an array -> always undefined ->
    // "No data". overall.mlb is the single populated record (matches the
    // player-page card, PlayerInfoCard) and is what the modal should show.
    const mlbRankVal = rankings?.overall?.mlb?.rank ?? null;
    const mlbRankTotal = rankings?.overall?.mlb?.total_players ?? null;
    const positionLabel = player?.info?.position ?? null;

    // Tile 6: League Rank — overall league scope. categories.position_league is a
    // LIST and is EMPTY for free agents (no rostered position records), so it
    // could never populate the tile. overall.league carries the league-wide rank
    // and works for free agents and rostered players alike.
    const leagueRank = rankings?.overall?.league?.rank ?? null;
    const leagueTotal = rankings?.overall?.league?.total_players ?? null;

    // Tile 7: ROS Projection — TRUE rest-of-season (P9 v3 `projections_ros`), the
    // SAME quantity the Overview tab's "Rest of Season (proj)" row shows.
    // DD-MODAL P2 fix (2026-07-15): was rendering `projected_full` (full-season)
    // under this "ROS Projection" label, so one modal showed two different numbers
    // for ROS (grid 45 HR vs Overview 17 HR for the same player). Now both read
    // `projections_ros`, and the hitter OPS is pinned to P9's published_forward_ops
    // — the one number Overview pins — so the two surfaces cannot disagree.
    const projections = analytics?.projections;
    const noRankFallback = 'Awaiting data';
    const projValue = (() => {
      if (!projections) return noRankFallback;
      if (pitcher) {
        // ROS reads projections_ros ONLY (no talent fallback). Missing → noRankFallback
        // ("Awaiting data") so a broken daily refresh is visible, not masked.
        const pg = projections?.stats_snapshot?.stats_data?.pitching;
        const p = pg?.projections_ros;
        if (!p) return noRankFallback;
        const era = p.era != null ? formatERA(p.era) : null;
        const k = p.strikeouts_pitched != null ? p.strikeouts_pitched : null;
        const whip = p.whip != null ? formatBattingAvg(p.whip) : null;
        return [era && `${era} ERA`, k && `${k} K`, whip && `${whip} WHIP`].filter(Boolean).join(' · ') || noRankFallback;
      }
      const hg = projections?.stats_snapshot?.stats_data?.hitting;
      const h = hg?.projections_ros;
      if (!h) return noRankFallback;
      const hr = h.home_runs != null ? h.home_runs : null;
      const avg = h.batting_avg != null ? formatBattingAvg(h.batting_avg) : null;
      // OPS = P9's published forward number (the one number Overview pins); ROS OPS fallback.
      const opsVal = hg?.published_forward_ops?.ops ?? h.ops;
      const ops = opsVal != null ? formatBattingAvg(opsVal) : null;
      return [hr != null && `${hr} HR`, avg && `${avg} AVG`, ops && `${ops} OPS`].filter(Boolean).join(' · ') || noRankFallback;
    })();

    // Tile 8: Fantasy Value — a CORRECT fantasy value for EVERY player (C44
    // correction, 2026-07-14): qualified players show the true FantasyService
    // grade; unqualified-active players show the small_sample-FLAGGED grade
    // (real value, measured vs the qualified pool — fantasy.small_sample +
    // fantasy.percentile_caveat from the qualified=false machinery) with the
    // caveat in the sub-line. The optimizer fit score NEVER renders here under
    // any label — and this 3x4 grid has no clean slot for a separate Roster
    // Fit cell, so the grid stays grade-only (fit renders under its honest
    // name in PlayerAdvancedAnalytics + mobile SummaryTab).
    const fantasy = analytics.fantasy;
    // Subtitle shows a true value PEER ("comparable to {name}") — the closest
    // same-position player by value (analytics.value_comparable), NOT the roster
    // optimizer's swap-out target (which produced the absurd "vs {injured stud}").
    const valueComp = analytics.value_comparable;
    let fantasyValue;
    let fantasySub;
    const fantasyLabel = 'Fantasy Value';
    // B1 (2026-07-14): the headline is the LETTER GRADE + rating context —
    // NEVER a dollar figure (a price here is redundant with the Market Value
    // tile and reads as worthless). Small-sample caveat machinery intact.
    if (fantasy?.letter_grade) {
      fantasyValue = fantasy.letter_grade;
      if (fantasy?.small_sample === true) {
        // Flagged small-sample grade: the caveat owns the sub-line.
        const gp = stats.games_played;
        fantasySub = gp != null ? `small sample · ${gp} games` : 'small sample · vs qualified pool';
      } else {
        // DD-RO-BUGS6-0725 B4: name the basis. The grade is total ROTO CATEGORY VALUE across every
        // scoring category (volume + rate), so a low-IP ace can grade mid — legible only if the sub
        // says what it measures. Peer-comp stays (it explains WHERE he lands); the percentile-only
        // fallback now reads "roto value · Nth pctl" instead of a bare percentile.
        const fantasyPctl = fantasy?.percentile != null ? `roto value · ${Math.round(fantasy.percentile)}th pctl` : 'roto value across scoring cats';
        fantasySub = valueComp?.player_name
          ? `roto value · like ${valueComp.player_name}`
          : fantasyPctl;
      }
    } else {
      fantasyValue = '—';
      fantasySub = 'not enough games for a grade';
    }

    // === Row 3: Context (adaptive) ===

    // Streaks (both owned + FA).
    // Pitchers get a pitcher-specific streak from the backend (consecutive QS for
    // SP, scoreless for RP) carried as `label`; hitters use hit/on-base streaks.
    const streakData = analytics.streaks;
    const hitStreak = streakData?.hit_streak || 0;
    const obStreak = streakData?.on_base_streak || 0;
    const streakLabel = pitcher
      ? (streakData?.label || 'No active streak')
      : (hitStreak > 0 ? `${hitStreak}G hit` : obStreak > 0 ? `${obStreak}G OB` : 'No active streak');

    // Owned tiles
    const salary = financial.contract_salary;
    const contractYears = financial.contract_years;
    const marketValue = financial.current_market_value;
    const surplus = marketValue != null && marketValue > 0 && salary != null ? Math.round(marketValue - salary) : null;

    // FA tiles
    const baselinePrice = financial.baseline_price;
    const hotnessScore = analytics.hotness_score;
    const hotnessDate = analytics.hotness_date;
    const hotnessStale = hotnessDate ? (Date.now() - new Date(hotnessDate).getTime()) > 14 * 24 * 60 * 60 * 1000 : false;
    const hotnessValue = hotnessScore != null ? `${Math.round(hotnessScore)}/100` : '--';
    const hotnessBadge = hotnessScore != null
      ? (hotnessScore > 60 ? 'text-red-400' : hotnessScore < 40 ? 'text-blue-400' : dynastyTheme.classes.text.neutralLight)
      : '';

    // Accent-bordered cell component
    const Cell = ({ label, value, sub, color, accentColor = cardBorder }) => {
      const isArray = Array.isArray(value);
      const displayLen = isArray ? value.join(' ').length : (value ? value.length : 0);
      return (
        <div style={{
          background: 'rgba(23,23,23,0.5)',
          borderRadius: 5,
          border: `1px solid ${cardBorder}`,
          borderLeft: `3px solid ${accentColor}`,
          padding: '5px 13px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: '57px',
        }}>
          <div style={{ flex: 1 }}>
            <div className={`${dynastyTheme.classes.text.neutral}`}
                 style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>
              {label}
            </div>
            {isArray ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 1 }}>
                {value.map((v, i) => (
                  <React.Fragment key={i}>
                    <span className={color || dynastyTheme.classes.text.white}
                          style={{ fontSize: displayLen > 12 ? 17 : 21, fontWeight: 400, lineHeight: 1.2 }}>
                      {v}
                    </span>
                    {i < value.length - 1 && (
                      <span className={dynastyTheme.classes.text.primary}
                            style={{ fontSize: 48, lineHeight: 0.3 }}>
                        ·
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className={color || dynastyTheme.classes.text.white}
                   style={{ fontSize: displayLen > 18 ? 11 : displayLen > 12 ? 13 : 18, fontWeight: 400, lineHeight: 1.2, marginTop: 1 }}>
                {value}
              </div>
            )}
          </div>
          {sub && (
            <div className={dynastyTheme.classes.text.neutralLight}
                 style={{ textAlign: 'right' }}>
              {typeof sub === 'string' && sub.includes('\n') ? (
                sub.split('\n').map((line, i) => (
                  <div key={i} style={{ fontSize: i === 0 ? 13 : 9, fontWeight: i === 0 ? 600 : 400, lineHeight: 1.3 }}>
                    {line}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 10 }}>{sub}</div>
              )}
            </div>
          )}
        </div>
      );
    };

    // Build rows with accent colors
    const row1 = [
      { label: pitcher ? 'Stuff' : 'Power', value: powerValue, accentColor: accent.info },
      { label: pitcher ? 'Command' : 'Discipline', value: discValue, accentColor: accent.primary },
      { label: 'Consistency', value: consistencyGrade, sub: consistencySub, accentColor: accent.success },
      { label: 'Trend', value: overallTrend, sub: trendSub || trendDetails, accentColor: accent.error },
    ];

    // Ranking tile — custom two-line compact layout (no uppercase, year + position inline)
    const RankCell = ({ scope, rankVal, rankTotal, accentColor }) => (
      <div style={{
        background: 'rgba(23,23,23,0.5)',
        borderRadius: 5,
        border: `1px solid ${cardBorder}`,
        borderLeft: `3px solid ${accentColor}`,
        padding: '5px 13px',
        minHeight: '57px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <span className={dynastyTheme.classes.text.neutral} style={{ fontSize: 11, fontWeight: 600 }}>
            {pitcher ? 'Pitcher' : 'Hitter'} Rank - {scope} ({statsSeasonYear})
          </span>
          {positionLabel && <span className={dynastyTheme.classes.text.neutral} style={{ fontSize: 11, fontWeight: 600 }}>Pos - {positionLabel}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span className={dynastyTheme.classes.text.white} style={{ fontSize: 18, fontWeight: 400, lineHeight: 1.2 }}>
            {rankVal != null ? `#${rankVal}` : 'No data'}
          </span>
          {rankVal != null && rankTotal ? (
            <span className={dynastyTheme.classes.text.neutralLight} style={{ fontSize: 10 }}>of {rankTotal}</span>
          ) : null}
        </div>
      </div>
    );

    const row2 = [
      { customRender: <RankCell key="mlb-rank" scope="MLB" rankVal={mlbRankVal} rankTotal={mlbRankTotal} accentColor={accent.neutral} /> },
      { customRender: <RankCell key="league-rank" scope="League" rankVal={leagueRank} rankTotal={leagueTotal} accentColor={accent.neutral} /> },
      { label: 'ROS Projection', value: projValue, accentColor: accent.neutral },
      { label: fantasyLabel, value: fantasyValue, sub: fantasySub, accentColor: accent.neutral },
    ];

    const row3 = isOwned && leagueId ? [
      { label: 'Salary', value: salary != null ? `$${salary}` : '--', sub: formatContractDisplay(financial.contract_type, contractYears), accentColor: accent.success },
      { label: 'Mkt Value', value: marketValue > 0 ? `${marketValue > baselinePrice ? '▲' : marketValue < baselinePrice ? '▼' : ''}$${marketValue}` : 'N/A', sub: baselinePrice ? `$${baselinePrice} base` : '', color: marketValue > 0 && baselinePrice ? (marketValue > baselinePrice ? dynastyTheme.classes.text.success : marketValue < baselinePrice ? dynastyTheme.classes.text.error : '') : '', accentColor: accent.primary },
      { label: 'Surplus', value: surplus != null ? `${surplus >= 0 ? '+' : ''}$${surplus}` : 'N/A', color: surplus != null ? (surplus >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error) : '', accentColor: accent.neutral },
      { label: 'Streak', value: streakLabel, accentColor: accent.neutral },
    ] : [
      { label: 'Base Price', value: baselinePrice ? `$${baselinePrice}` : '--', accentColor: accent.success },
      { label: 'Mkt Value', value: marketValue ? `${marketValue > baselinePrice ? '▲' : marketValue < baselinePrice ? '▼' : ''}$${marketValue}` : '--', sub: baselinePrice ? `$${baselinePrice} base` : '', color: marketValue && baselinePrice ? (marketValue > baselinePrice ? dynastyTheme.classes.text.success : marketValue < baselinePrice ? dynastyTheme.classes.text.error : '') : '', accentColor: accent.primary },
      { label: 'Hotness', value: hotnessValue, color: hotnessBadge, sub: hotnessStale ? 'Offseason' : hotnessScore > 60 ? 'hot' : hotnessScore < 40 ? 'cold' : 'neutral', accentColor: accent.neutral },
      { label: 'Streak', value: streakLabel, accentColor: accent.neutral },
    ];

    return (
      <div style={{ padding: '3px 16px' }}>
        {[row1, row2, row3].map((row, ri) => (
          <div key={ri} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 3, marginBottom: ri < 2 ? 3 : 0 }}>
            {row.map((cell, ci) => cell.customRender || <Cell key={cell.label || ci} {...cell} />)}
          </div>
        ))}
      </div>
    );
};

export default AnalyticsGrid;
